import React, { useState, useEffect } from 'react';
import { X, History, User, Clock, Edit, Plus, Trash2, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';

interface AuditLogEntry {
  id: string;
  action: string; // 'create', 'update', 'delete', 'read'
  resource_type: string;
  resource_id: string;
  resource_name?: string; // e.g. "Autorizado via app por João" — the specific description of what happened
  user_id: string;
  username?: string;  // Backend returns "username" not "user_name"
  user_role?: string;
  timestamp: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  changes?: Array<{
    field: string;
    old_value: any;
    new_value: any;
  }>;
  ip_address?: string;
  user_agent?: string;
  compliance_category?: string;
}

interface ModificationHistoryModalProps {
  personId: string;
  personName: string;
  onClose: () => void;
  darkMode?: boolean;
}

export const ModificationHistoryModal: React.FC<ModificationHistoryModalProps> = ({
  personId,
  personName,
  onClose,
  darkMode = false,
}) => {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState<string>('all');

  useEffect(() => {
    fetchAuditTrail();
  }, [personId]);

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      // Use the audit trail endpoint for this specific person
      console.log('[ModificationHistory] Fetching audit trail for person:', personId);
      const response = await api.get(`/audit/trail/person/${personId}`);
      console.log('[ModificationHistory] API response:', response);
      console.log('[ModificationHistory] Response data:', response.data);

      // The API returns { audit_trail: [], resource_id: '', resource_type: '', total: 0 }
      const logs = response.data?.audit_trail || [];
      console.log('[ModificationHistory] Parsed logs:', logs);

      if (Array.isArray(logs)) {
        console.log('[ModificationHistory] Found', logs.length, 'audit log entries');
        // Log first entry to see structure
        if (logs.length > 0) {
          console.log('[ModificationHistory] First entry structure:', logs[0]);
          console.log('[ModificationHistory] user_id:', logs[0]?.user_id);
          console.log('[ModificationHistory] username:', logs[0]?.username);
          console.log('[ModificationHistory] user_role:', logs[0]?.user_role);
        }
      } else {
        console.warn('[ModificationHistory] Logs is not an array:', logs);
      }

      setAuditLogs(Array.isArray(logs) ? logs : []);
    } catch (err: any) {
      console.error('[ModificationHistory] Failed to fetch audit trail:', err);
      console.error('[ModificationHistory] Error response:', err.response);
      console.error('[ModificationHistory] Error data:', err.response?.data);
      console.error('[ModificationHistory] Error status:', err.response?.status);

      // Show more specific error message
      const errorMsg = err.response?.status === 404
        ? 'Endpoint de auditoria não encontrado'
        : err.response?.data?.error || 'Erro ao carregar histórico de modificações';

      toast.error(errorMsg);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create': return <Plus className="w-4 h-4 text-green-600" />;
      case 'update': return <Edit className="w-4 h-4 text-blue-600" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-600" />;
      default: return <History className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      create: 'Criado',
      update: 'Atualizado',
      delete: 'Excluído',
      read: 'Visualizado',
    };
    return labels[action] || action;
  };

  const formatFieldName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      first_name: 'Nome',
      last_name: 'Sobrenome',
      email: 'E-mail',
      phone: 'Telefone',
      cpf: 'CPF',
      birth_date: 'Data de Nascimento',
      is_active: 'Status Ativo',
      employee_no: 'Número de Funcionário',
      document_no: 'Número do Documento',
      document_type: 'Tipo de Documento',
      identity_photo_url: 'Foto de Identidade',
      profile_picture_url: 'Foto de Perfil',
    };
    return fieldNames[field] || field;
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const filteredLogs = filterAction === 'all'
    ? auditLogs
    : auditLogs.filter(log => log.action === filterAction);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          "rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col",
          darkMode ? "bg-gray-800" : "bg-white"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn(
          "p-6 border-b flex items-start justify-between gap-4",
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        )}>
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <History className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h2 className={cn(
                "text-2xl font-bold truncate",
                darkMode ? "text-gray-100" : "text-gray-900"
              )}>
                Histórico de Modificações
              </h2>
              <p className={cn(
                "text-sm mt-1",
                darkMode ? "text-gray-400" : "text-gray-600"
              )}>
                {personName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              "p-2 rounded-lg transition-colors flex-shrink-0",
              darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
            )}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className={cn(
          "p-4 border-b",
          darkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
        )}>
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-sm font-medium",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              Filtrar por ação:
            </span>
            <div className="flex gap-2">
              {['all', 'create', 'update', 'delete'].map((action) => (
                <button
                  key={action}
                  onClick={() => setFilterAction(action)}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-lg font-medium transition-colors",
                    filterAction === action
                      ? "bg-blue-600 text-white"
                      : darkMode
                      ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                      : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                  )}
                >
                  {action === 'all' ? 'Todas' : getActionLabel(action)}
                </button>
              ))}
            </div>
            <button
              onClick={fetchAuditTrail}
              className={cn(
                "ml-auto p-2 rounded-lg transition-colors",
                darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
              )}
              title="Atualizar"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className={cn(
                "ml-3 text-sm",
                darkMode ? "text-gray-400" : "text-gray-600"
              )}>
                Carregando histórico...
              </span>
            </div>
          ) : filteredLogs.length > 0 ? (
            <div className="space-y-4">
              {filteredLogs.map((log, index) => (
                <div
                  key={log.id}
                  className={cn(
                    "relative pl-8 pb-4",
                    index !== filteredLogs.length - 1 && "border-l-2",
                    darkMode ? "border-gray-700" : "border-gray-200"
                  )}
                >
                  {/* Timeline dot */}
                  <div className={cn(
                    "absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full flex items-center justify-center",
                    darkMode ? "bg-gray-800" : "bg-white"
                  )}>
                    {getActionIcon(log.action)}
                  </div>

                  {/* Log card */}
                  <div className={cn(
                    "rounded-lg border p-4",
                    darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                  )}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <p className={cn(
                          "font-semibold",
                          darkMode ? "text-gray-100" : "text-gray-900"
                        )}>
                          {getActionLabel(log.action)}
                        </p>
                        {log.resource_name && (
                          <p className={cn(
                            "text-sm mt-0.5",
                            darkMode ? "text-gray-300" : "text-gray-700"
                          )}>
                            {log.resource_name}
                          </p>
                        )}
                        <div className={cn(
                          "flex items-center gap-2 text-sm mt-1",
                          darkMode ? "text-gray-400" : "text-gray-600"
                        )}>
                          <User className="w-3.5 h-3.5" />
                          <span>{log.username || 'Sistema'}</span>
                          <span>•</span>
                          <Clock className="w-3.5 h-3.5" />
                          <span>
                            {new Date(log.timestamp).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Changes details */}
                    {log.changes && log.changes.length > 0 && (
                      <div className={cn(
                        "mt-3 pt-3 border-t",
                        darkMode ? "border-gray-700" : "border-gray-200"
                      )}>
                        <p className={cn(
                          "text-xs font-medium mb-2",
                          darkMode ? "text-gray-400" : "text-gray-600"
                        )}>
                          Campos alterados:
                        </p>
                        <div className="space-y-2">
                          {log.changes.map((change, idx) => (
                            <div
                              key={idx}
                              className={cn(
                                "text-sm p-2 rounded",
                                darkMode ? "bg-gray-800" : "bg-gray-50"
                              )}
                            >
                              <span className={cn(
                                "font-medium",
                                darkMode ? "text-gray-300" : "text-gray-700"
                              )}>
                                {formatFieldName(change.field)}:
                              </span>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={cn(
                                  "px-2 py-1 rounded text-xs",
                                  darkMode ? "bg-red-900/30 text-red-400" : "bg-red-50 text-red-700"
                                )}>
                                  {formatValue(change.old_value)}
                                </span>
                                <span className={cn(
                                  darkMode ? "text-gray-500" : "text-gray-400"
                                )}>
                                  →
                                </span>
                                <span className={cn(
                                  "px-2 py-1 rounded text-xs",
                                  darkMode ? "bg-green-900/30 text-green-400" : "bg-green-50 text-green-700"
                                )}>
                                  {formatValue(change.new_value)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <History className={cn(
                "w-12 h-12 mb-3",
                darkMode ? "text-gray-600" : "text-gray-300"
              )} />
              <p className={cn(
                "text-sm",
                darkMode ? "text-gray-400" : "text-gray-600"
              )}>
                Nenhum registro encontrado
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={cn(
          "p-4 border-t flex justify-end",
          darkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
        )}>
          <button
            onClick={onClose}
            className={cn(
              "px-4 py-2 rounded-lg font-medium transition-colors",
              darkMode
                ? "border border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border border-gray-300 text-gray-700 hover:bg-gray-100"
            )}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
